/**
 * printerGateStore — Compuerta global de impresión sin impresora.
 *
 * QUÉ HACE:
 *  - Centraliza el aviso "no hay impresora conectada" como MODAL global con
 *    confirmación: la acción final de imprimir NO se ejecuta hasta que el
 *    usuario confirma (o hasta que marcó «no volver a mostrar»).
 *  - `ask(tipo)` devuelve una promesa que resuelve true si el usuario confirma
 *    imprimir (o el aviso está suprimido) y false si cancela.
 *  - La supresión «no volver a mostrar» persiste en transactionalStore
 *    (clave `pos_print_warn_suppressed`, por dispositivo).
 *
 * QUÉ EXPORTA:
 *  - usePrinterGateStore (hook zustand)
 *  - askPrintWithoutPrinter(tipo): helper imperativo para lib/flows (posHardware,
 *    watchers) que no pueden usar hooks.
 *
 * QUÉ NO DEBE HACER:
 *  - No renderiza UI (eso vive en PrinterUnavailableModal, features/caja).
 *  - No decide qué se imprime ni cómo; solo pregunta antes del fallback.
 */
import { create } from "zustand";
import { txRead, txWrite } from "@/lib/persistence/transactionalStore";

/** Clave de supresión persistida: «No volver a mostrar este aviso». */
const SUPPRESS_KEY = "pos_print_warn_suppressed";

/**
 * Contexto de la compuerta: adapta el mensaje y el botón de confirmación.
 * - "venta": cobro en curso (COBRAR) → «Vender igual» / «vender sin recibo».
 * - "documento": impresión manual de cualquier documento → «Sí, imprimir».
 */
export type PrintGateContext = "venta" | "documento";

interface PrintGateRequest {
  /** Documento en cuestión (ej. "el recibo de venta"). */
  tipo: string;
  /** Resuelve la promesa de `ask` con la decisión del usuario. */
  resolve: (proceed: boolean) => void;
  /** Contexto para el texto y botón de confirmación del modal. */
  context: PrintGateContext;
}

interface PrinterGateState {
  /** Solicitud pendiente que el modal debe mostrar (null = modal cerrado). */
  request: PrintGateRequest | null;
  /** Pide confirmación; true = proceder (o suprimido), false = cancelar. */
  ask: (tipo: string, context?: PrintGateContext) => Promise<boolean>;
  /** El modal resolvió la decisión del usuario. */
  resolve: (proceed: boolean) => void;
  /** Persiste «no volver a mostrar este aviso». */
  suppressForever: () => void;
}

export const usePrinterGateStore = create<PrinterGateState>((set, get) => ({
  request: null,
  ask: (tipo, context = "documento") => {
    // Ya hay una solicitud pendiente: se descarta (equivale a cancelar).
    const prev = get().request;
    if (prev) {
      set({ request: null });
      prev.resolve(false);
    }
    if (txRead<boolean>(SUPPRESS_KEY, false)) {
      return Promise.resolve(true);
    }
    return new Promise<boolean>((resolve) => {
      set({ request: { tipo, context, resolve } });
    });
  },
  resolve: (proceed) => {
    const req = get().request;
    if (!req) return;
    set({ request: null });
    req.resolve(proceed);
  },
  suppressForever: () => {
    txWrite(SUPPRESS_KEY, true);
  },
}));

/**
 * Helper imperativo (sin hooks) para lib/ y flows que disparan impresión.
 * Ejemplo: `if (await askPrintWithoutPrinter("el recibo de venta")) window.print();`
 */
export function askPrintWithoutPrinter(
  tipo: string,
  context: PrintGateContext = "documento",
): Promise<boolean> {
  return usePrinterGateStore.getState().ask(tipo, context);
}
